import { useCallback, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { DataGrid } from "@mui/x-data-grid";

import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import { formatNumber } from "@/features/shared";

export const ProductVariantsSection = () => {
  const { showSnackbar } = useSnackbar();
  const { control, setValue, getValues } = useFormContext();

  const { fields: customerClaimProductVariants } = useFieldArray({
    control,
    name: "customerClaimProductVariants",
  });

  const handleRowSelectionChange = useCallback(
    (newSelection) => {
      const currentVariants = getValues("customerClaimProductVariants");
      const selectedIds = newSelection.ids || new Set();

      currentVariants.forEach((variant, idx) => {
        // Handle both 'include' and 'exclude' selection types
        const isSelected =
          newSelection.type === "include"
            ? selectedIds.has(variant.id)
            : !selectedIds.has(variant.id);

        // Only update if selection state changed
        if (variant.selected !== isSelected) {
          setValue(`customerClaimProductVariants.${idx}.selected`, isSelected, {
            shouldDirty: false,
          });
        }
      });
    },
    [getValues, setValue]
  );

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      // Coerce to numbers
      const quantity = Number(newRow.quantity);

      // Validate type/finite
      if (!Number.isFinite(quantity)) {
        throw new Error("Kuantitas tidak valid");
      }

      // Validate quantity is at least 1
      if (quantity < 1) {
        throw new Error("Kuantitas tidak boleh kurang dari 1");
      }

      // Validate quantity doesn't exceed the original quantity from RECEIVE claim
      if (quantity > oldRow.quantity) {
        throw new Error(
          `Kuantitas tidak boleh lebih dari ${oldRow.quantity} (kuantitas dari klaim terima)`
        );
      }

      // Find the index and update
      const idx = customerClaimProductVariants.findIndex(
        (v) => v.id === newRow.id
      );
      if (idx === -1) return oldRow;

      const updatedRow = {
        ...customerClaimProductVariants[idx],
        quantity,
        note: newRow.note || null,
      };

      setValue(`customerClaimProductVariants.${idx}`, updatedRow, {
        shouldDirty: true,
      });

      return updatedRow;
    },
    [customerClaimProductVariants, setValue]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Nama Barang",
        flex: 3,
        renderCell: ({ row: { name, sku } }) => (
          <ProductVariantNameCell name={name} sku={sku} />
        ),
      },
      {
        field: "quantity",
        headerName: "Kuantitas",
        type: "number",
        valueFormatter: (value) => formatNumber(value),
        flex: 1,
        editable: true,
      },
      {
        field: "unitOfMeasurement",
        headerName: "Satuan",
        flex: 1,
        valueGetter: (_, row) => row.product?.unitOfMeasurement?.name || "-",
      },
      {
        field: "note",
        headerName: "Catatan",
        flex: 2,
        editable: true,
      },
    ],
    []
  );

  return (
    <DataGrid
      rows={customerClaimProductVariants}
      columns={columns}
      disableRowSelectionOnClick
      hideFooter
      processRowUpdate={handleProcessRowUpdate}
      onProcessRowUpdateError={(error) => {
        showSnackbar(error.message);
      }}
      checkboxSelection
      onRowSelectionModelChange={handleRowSelectionChange}
    />
  );
};
